import Button from "@/components/common/Button";
import { useCourses } from "@/hooks/queries/useCourseQueries";
import { useAuth } from "@/hooks/useAuth";
import { useDepartmentData } from "@/hooks/queries/useDepartmentData";
import { useAssignCourseToDepartment, useRemoveCourseFromDepartment } from "@/hooks/queries/useCourseAssignmentMutations";
import {
  AcademicCapIcon,
  PlusIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";


interface DepartmentEmployee {
  id: string;
  name: string;
  email: string;
  role: string;
  isApproved: boolean;
}


const ManageCourses = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: allCourses = [] } = useCourses();

  // Get department data using React Query
  const {
    data: departmentData,
    isLoading: departmentLoading,
    error: departmentError,
  } = useDepartmentData(user?.departmentId || "", !!user?.departmentId);

  // Course assignment mutations
  const assignCourseMutation = useAssignCourseToDepartment();
  const removeCourseMutation = useRemoveCourseFromDepartment();

  // Debug logging
  console.log("ManageCourses - User:", user);
  console.log("ManageCourses - User role:", user?.role);
  console.log("ManageCourses - User department ID:", user?.departmentId);
  console.log("ManageCourses - All courses:", allCourses);

  // Check if user has access
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t("admin.noUserFound")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t("admin.pleaseLogin")}
          </p>
        </div>
      </div>
    );
  }

  // Check if user has correct role
  if (!["department_admin", "departmentAdmin"].includes(user.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t("admin.accessDenied")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t("admin.departmentAdminOnly")}
          </p>
          <p className="text-sm text-gray-500">
            {t("admin.currentRole")} {user.role}
          </p>
          <p className="text-sm text-gray-500">{t("admin.requiredRoles")}</p>
        </div>
      </div>
    );
  }

  const [departmentCourses, setDepartmentCourses] = useState<string[]>([]);
  const [departmentEmployees, setDepartmentEmployees] = useState<
    DepartmentEmployee[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [showCourseAssignment, setShowCourseAssignment] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);

  // Extract department data when available
  useEffect(() => {
    if (departmentData?.data?.document) {
      const deptData = departmentData.data.document;
      setDepartmentCourses(deptData.coursesAssignedToDepartment || []);
      setDepartmentEmployees(deptData.employees || []);
    }
  }, [departmentData]);

  // Handle course assignment
  const handleAssignCourses = async () => {
    if (selectedCourseIds.length === 0) {
      toast.error(t("admin.pleaseSelectCourses"));
      return;
    }

    setLoading(true);
    try {
      // Make API calls to assign each course individually
      for (const courseId of selectedCourseIds) {
        await assignCourseMutation.mutateAsync({
          courseId,
          departmentId: user?.departmentId || "",
        });
      }

      // Update local state
      const newDepartmentCourses = [...departmentCourses, ...selectedCourseIds];
      setDepartmentCourses(newDepartmentCourses);

      toast.success(t("admin.coursesAssignedSuccess"));
      setShowCourseAssignment(false);
      setSelectedCourseIds([]);
    } catch (error) {
      console.error("Failed to assign courses:", error);
      toast.error(t("admin.failedToAssignCourses"));
    } finally {
      setLoading(false);
    }
  };

  // Handle course removal
  const handleRemoveCourse = async (courseId: string) => {
    try {
      // Make API call to remove course from department
      await removeCourseMutation.mutateAsync({
        courseId,
        departmentId: user?.departmentId || "",
      });

      // Update local state
      const newDepartmentCourses = departmentCourses.filter(
        (id) => id !== courseId
      );
      setDepartmentCourses(newDepartmentCourses);

      toast.success(t("admin.courseRemovedSuccess"));
    } catch (error) {
      console.error("Failed to remove course:", error);
      toast.error(t("admin.failedToRemoveCourse"));
    }
  };

  // Handler for course selection change
  const handleCourseSelectionChange = (e: React.ChangeEvent<HTMLInputElement>, courseId: string) => {
    if (e.target.checked) {
      setSelectedCourseIds([...selectedCourseIds, courseId]);
    } else {
      setSelectedCourseIds(selectedCourseIds.filter(id => id !== courseId));
    }
  };

  // Handler for retry button
  const handleRetry = () => {
    window.location.reload();
  };

  // Handler for go to dashboard
  const handleGoToDashboard = () => {
    window.location.href = "/dashboard";
  };

  // Handler for show course assignment
  const handleShowCourseAssignment = () => {
    setShowCourseAssignment(true);
  };

  // Handler for hide course assignment
  const handleHideCourseAssignment = () => {
    setShowCourseAssignment(false);
  };

  // Show loading state while department data is being fetched
  if (departmentLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {t("admin.loadingDepartmentData")}
          </p>
        </div>
      </div>
    );
  }

  // Show error state if department data failed to load
  if (departmentError && !departmentData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t("admin.unableToLoadDepartment")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {departmentError?.message || t("admin.departmentLoadError")}
          </p>
          <Button
            variant="primary"
            onClick={handleRetry}
            className="mr-2"
          >
            {t("admin.retry")}
          </Button>
          <Button
            variant="outline"
            onClick={handleGoToDashboard}
          >
            {t("admin.goToDashboard")}
          </Button>
        </div>
      </div>
    );
  }

  // If no department found, show appropriate message
  if (!departmentData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t("admin.departmentNotFound")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t("admin.departmentNotFoundDescription")}
          </p>
          <Button
            variant="outline"
            onClick={handleGoToDashboard}
          >
            {t("admin.goToDashboard")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2 break-words">
          {t("admin.manageDepartment")}: {departmentData.data.document.name}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          {t("admin.manageDepartmentDescription")}
        </p>
      </div>

      {/* Course Assignment Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 sm:p-6 mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3 sm:gap-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            {t("admin.departmentCourses")}
          </h2>
          <Button
            variant="primary"
            leftIcon={<PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />}
            onClick={handleShowCourseAssignment}
            className="w-full sm:w-auto"
          >
            {t("admin.assignCourses")}
          </Button>
        </div>

        {departmentCourses.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <AcademicCapIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-4" />
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-4">
              {t("admin.noCoursesAssigned")}
            </p>
            <Button
              variant="outline"
              onClick={handleShowCourseAssignment}
              className="w-full sm:w-auto"
            >
              {t("admin.assignFirstCourse")}
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {departmentCourses.map((courseId) => {
              const course = allCourses.find((c) => c._id === courseId);
              return (
                <div
                  key={courseId}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2 break-words">
                        {course?.title?.en || t("admin.unknownCourse")}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                        {course?.courseDescription?.en ||
                          t("admin.noDescriptionAvailable")}
                      </p>
                      <div className="flex items-center text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        <span className="capitalize">
                          {course?.courseLevel || t("admin.levelNotSpecified")}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveCourse(courseId)}
                      className="ml-2 flex-shrink-0"
                    >
                      <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Department Employees Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {t("admin.departmentEmployees")} ({departmentEmployees.length})
        </h2>

        {departmentEmployees.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <UsersIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-4" />
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
              {t("admin.noEmployeesAssigned")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Desktop Table */}
            <table className="hidden sm:table min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.employee")}
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.email")}
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.role")}
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.status")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {departmentEmployees.map((employee) => (
                  <tr key={employee.id}>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {employee.name || t("admin.unknownEmployee")}
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {employee.email}
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                        {employee.role}
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          employee.isApproved
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                        }`}
                      >
                        {employee.isApproved
                          ? t("admin.approved")
                          : t("admin.pending")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="sm:hidden space-y-3">
              {departmentEmployees.map((employee) => (
                <div key={employee.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {employee.name || t("admin.unknownEmployee")}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {employee.email}
                      </p>
                    </div>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ml-2 ${
                        employee.isApproved
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                      }`}
                    >
                      {employee.isApproved
                        ? t("admin.approved")
                        : t("admin.pending")}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {t("admin.role")}: {employee.role}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Course Assignment Modal */}
      {showCourseAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {t("admin.assignCoursesToDepartment")}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleHideCourseAssignment}
              >
                <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </div>

            <div className="mb-4">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t("admin.selectCoursesDescription")}
              </p>

              <div className="grid gap-2 sm:gap-3 max-h-60 overflow-y-auto">
                {(() => {
                  const availableCourses = allCourses.filter(
                    (course) => !departmentCourses.includes(course._id)
                  );

                  if (availableCourses.length === 0) {
                    return (
                      <div className="text-center py-6 sm:py-8">
                        <AcademicCapIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-4" />
                        <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                          {t("admin.allCoursesAssigned")}
                        </p>
                      </div>
                    );
                  }

                  return availableCourses.map((course) => (
                    <label
                      key={course._id}
                      className="flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCourseIds.includes(course._id)}
                        onChange={(e) => handleCourseSelectionChange(e, course._id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm sm:text-base font-medium text-gray-900 dark:text-white break-words">
                          {course.title?.en || t("admin.unknownCourse")}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                          {course.courseDescription?.en ||
                            t("admin.noDescription")}
                        </div>
                      </div>
                    </label>
                  ));
                })()}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sm:space-x-3">
              <Button
                variant="outline"
                onClick={handleHideCourseAssignment}
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                {t("admin.cancel")}
              </Button>
              <Button
                variant="primary"
                onClick={handleAssignCourses}
                disabled={loading || selectedCourseIds.length === 0}
                className="w-full sm:w-auto order-1 sm:order-2"
              >
                {loading
                  ? t("admin.assigning")
                  : `${t("admin.assignCourses")} ${selectedCourseIds.length} ${
                      selectedCourseIds.length !== 1
                        ? t("admin.courses")
                        : t("admin.course")
                    }`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCourses;
